import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { reportError } from "@/lib/reportError";
import { isUnverifiedFactWrite, rememberFamilyFact } from "@/lib/rememberFamilyFact";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { usePreferences } from "@/hooks/useUserPreferences";
import { showSaveFailedToast } from "@/lib/saveFailureToast";
import { useStatusBar } from "@/hooks/useStatusBar";
import KinAvatar from "./KinAvatar";
import { KID_COLOR_PALETTE, type KidColorRecord } from "@/lib/kidColors";
import {
  KID_GENDER_FACT_KEY,
  KID_GENDER_LABELS,
  KID_GENDER_VALUES,
  type KidGender,
} from "@/lib/kidGender";

interface KidsCrewSetupProps {
  /** Called after Next saves OR when Skip is tapped — both proceed to next step. */
  onComplete: (skipped?: boolean) => void;
}

interface KidEntry {
  name: string;
  birthday: string; // YYYY-MM-DD or ""
  /**
   * #1853 — "boy" | "girl", or null for not answered.
   *
   * ⚠️ null is the DEFAULT and must stay a real answer, not a prompt she has
   * to dismiss. Skipping the whole sheet already works; skipping this one
   * field has to work the same way, and Kin falls back to "they".
   */
  gender: KidGender | null;
}

const MAX_KIDS = 6;

/** Trim a kid's name, preserving the exact casing the user typed. */
function cleanName(raw: string): string {
  return String(raw || "").trim();
}

const KidsCrewSetup = ({ onComplete }: KidsCrewSetupProps) => {
  useStatusBar({ color: "F0F0FF", style: "dark" });
  const { user } = useAuth();
  const { updatePreferences } = usePreferences();
  const [kids, setKids] = useState<KidEntry[]>([
    { name: "", birthday: "", gender: null },
    { name: "", birthday: "", gender: null },
  ]);
  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const animateOut = (cb: () => void) => {
    setVisible(false);
    setTimeout(cb, 300);
  };

  const updateName = (idx: number, value: string) => {
    setKids((prev) => prev.map((k, i) => (i === idx ? { ...k, name: value } : k)));
  };

  const updateBirthday = (idx: number, value: string) => {
    setKids((prev) => prev.map((k, i) => (i === idx ? { ...k, birthday: value } : k)));
  };

  /** Tapping the selected choice again clears it back to "not answered". */
  const updateGender = (idx: number, value: KidGender) => {
    setKids((prev) =>
      prev.map((k, i) => (i === idx ? { ...k, gender: k.gender === value ? null : value } : k)),
    );
  };

  const removeField = (idx: number) => {
    setKids((prev) => {
      if (prev.length === 1) return [{ name: "", birthday: "", gender: null }];
      return prev.filter((_, i) => i !== idx);
    });
  };

  const addField = () => {
    setKids((prev) =>
      prev.length >= MAX_KIDS ? prev : [...prev, { name: "", birthday: "", gender: null }],
    );
  };

  const handleNext = async () => {
    if (saving || !user) return;
    setSaving(true);

    // Collect non-empty trimmed unique (case-insensitive) names, exact casing preserved.
    // Preserve birthday AND gender (#1853) paired with each cleaned name.
    const seen = new Set<string>();
    const cleaned: Array<{ name: string; birthday: string; gender: KidGender | null }> = [];
    for (const kid of kids) {
      const c = cleanName(kid.name);
      if (!c) continue;
      const key = c.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      cleaned.push({ name: c, birthday: kid.birthday, gender: kid.gender });
    }

    if (cleaned.length === 0) {
      // Nothing to save — same as Skip.
      setSaving(false);
      animateOut(() => onComplete(true));
      return;
    }

    const cleanedNames = cleaned.map((k) => k.name);

    // Auto-assign colors using the existing KID_COLOR_PALETTE order.
    const colors: KidColorRecord = {};
    cleanedNames.forEach((name, i) => {
      colors[name] = KID_COLOR_PALETTE[i % KID_COLOR_PALETTE.length];
    });

    // ⚠️ #38 — through the provider, NOT a direct upsert.
    //
    // This was the last of the six kid-name sites still talking to the table
    // itself. The provider upserts with the same `onConflict: "user_id"` and
    // stamps the same `updated_at`, so the write is identical — but it also
    // updates the shared `prefs` cache, so every consumer sees the new crew
    // without waiting for a refetch.
    //
    // ⚠️ `updatePreferences` RESOLVES its error rather than throwing, so the
    // try/catch this replaced could never have fired (#545). `const error =
    // await …` is the only form that works, and this call DOES claim success
    // on screen, which makes reading it mandatory rather than optional.
    const error = await updatePreferences({
      known_kid_names: cleanedNames,
      known_kid_colors: colors as any,
    });
    if (error) {
      // failure reports must be error-level: Sentry captures only "error" (see main.tsx captureConsoleIntegration).
      reportError("[KidsCrew] kid_names save failed", error, {
        tags: { area: "kids-crew-setup", writer: "kid_names" },
        extra: { code: (error as any)?.code, details: (error as any)?.details, hint: (error as any)?.hint },
      });
      showSaveFailedToast();
      setSaving(false);
      return;
    }
    toast(`Got it. ${cleanedNames.length === 1 ? cleanedNames[0] : "Your crew"} is on the list. 💜`, {
      style: {
        background: "#1C1A3E",
        color: "#FFFFFF",
        border: "1px solid #5050C8",
      },
    });

    // Fire-and-forget: write birthdays to family_context (non-blocking).
    // A failed write here never surfaces to the user — names are already saved.
    //
    // #1482 — ONE RPC PER KID, deliberately not one multi-row insert.
    //
    // This was `.insert(rows)` with every kid in a single statement. Two
    // problems, and the second is why the shape changed rather than just the
    // call:
    //
    //   1. `birthdate` is named in
    //      20260810000000_family_context_allow_multivalued_keys.sql as a key
    //      that STAYS single-valued, and this is not the only writer of it —
    //      ProSetupFlowModal and MyKidsSection write the same (kid, birthdate)
    //      pair. A mom who does Kids Crew Setup and then Pro setup, in either
    //      order, collides on the second one.
    //   2. Postgres fails the WHOLE statement on a single conflict, so one
    //      kid's birthday already existing lost every OTHER kid's birthday in
    //      the same call, including brand-new ones that would have inserted.
    //
    // upsert_family_fact resolves each one server-side, so a collision is an
    // overwrite rather than an error, and one kid cannot take the others down.
    //
    // Still fire-and-forget: her names are already saved and the sheet closes
    // immediately, so this must not put the network in front of the animation.
    const kidsWithBirthdays = cleaned.filter((k) => k.birthday);
    if (kidsWithBirthdays.length > 0) {
      void (async () => {
        for (const k of kidsWithBirthdays) {
          const result = await rememberFamilyFact(
            { category: "kid", subject: k.name, key: "birthdate", value: String(k.birthday) },
            "onboarding",
            "high",
          );
          // rememberFamilyFact never throws — it returns the failure so a caller
          // can decide. `ok` is the only honest signal: the RPC reports refusals
          // as a returned action with no Postgres error.
          if (!result.ok) {
            // failure reports must be error-level: Sentry captures only "error" (see main.tsx captureConsoleIntegration).
            // #1857 — a lost response whose read-back could not answer is
            // titled "unverified", not "failed": the row may hold the value.
            reportError(
              `[KidsCrew] kid_birthday save ${isUnverifiedFactWrite(result) ? "unverified" : "failed"}`,
              Object.assign(
                new Error(
                  `upsert_family_fact returned ${result.action ?? "no action row"}${result.error ? `: ${result.error}` : ""}`,
                ),
                { code: result.action ?? "no_action_row", details: result.error },
              ),
              {
                tags: { area: "kids-crew-setup", writer: "kid_birthday" },
                extra: { code: result.action ?? "no_action_row", details: result.error, kid: k.name },
              },
            );
          }
        }
      })();
    }

    // #1853 — genders, same shape and the same reasoning as the birthdays
    // above: one RPC per kid (a multi-row insert fails the WHOLE statement on
    // one collision, and `gender` is single-valued so a repeat IS a
    // collision), fire-and-forget, failures to Sentry only.
    //
    // ⚠️ Deliberately NOT toasted on failure. The "Got it, your crew is on the
    // list" toast above fires over the NAMES write, which was read and did
    // land. A second toast here would interrupt a sheet that is already
    // animating out, over an optional field, in the first two minutes of the
    // app — and she has no way to act on it mid-animation. She can set it in
    // Settings, where the write IS confirmed per house rule 16.
    const kidsWithGenders = cleaned.filter((k) => k.gender);
    if (kidsWithGenders.length > 0) {
      void (async () => {
        for (const k of kidsWithGenders) {
          const result = await rememberFamilyFact(
            { category: "kid", subject: k.name, key: KID_GENDER_FACT_KEY, value: String(k.gender) },
            "onboarding",
            "high",
          );
          if (!result.ok) {
            // failure reports must be error-level: Sentry captures only "error" (see main.tsx captureConsoleIntegration).
            reportError(
              `[KidsCrew] kid_gender save ${isUnverifiedFactWrite(result) ? "unverified" : "failed"}`,
              Object.assign(
                new Error(
                  `upsert_family_fact returned ${result.action ?? "no action row"}${result.error ? `: ${result.error}` : ""}`,
                ),
                { code: result.action ?? "no_action_row", details: result.error },
              ),
              {
                tags: { area: "kids-crew-setup", writer: "kid_gender" },
                extra: { code: result.action ?? "no_action_row", details: result.error, kid: k.name },
              },
            );
          }
        }
      })();
    }

    setSaving(false);
    animateOut(() => onComplete(false));
  };

  const handleSkip = () => {
    if (saving) return;
    toast("No worries! Kin will autodetect your kids' names on Today. Or you can add them anytime in Settings. 💜");
    animateOut(() => onComplete(true));
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center transition-all duration-300"
      style={{
        backgroundColor: visible ? "rgba(28, 26, 62, 0.6)" : "rgba(28, 26, 62, 0)",
        backdropFilter: visible ? "blur(4px)" : "blur(0px)",
      }}
    >
      <div
        className="relative w-full mx-4 bg-white overflow-y-auto transition-all duration-400 ease-out"
        style={{
          maxWidth: 440,
          maxHeight: "90vh",
          borderRadius: 20,
          padding: "28px 20px",
          boxShadow: "0 24px 60px rgba(28, 26, 62, 0.25)",
          opacity: visible ? 1 : 0,
          transform: visible ? "scale(1)" : "scale(0.95)",
        }}
      >
        <div className="flex flex-col items-center">
          <div className="animate-float mb-4">
            <KinAvatar size="md" variant="dark" />
          </div>

          <h1
            className="text-center"
            style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 26,
              color: "#1C1A3E",
              marginBottom: 6,
            }}
          >
            Who's in your crew?
          </h1>
          <p
            className="text-center"
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 15,
              color: "#54525C",
              lineHeight: 1.4,
              marginBottom: 18,
              padding: "0 4px",
            }}
          >
            Add your kids so Kin can color-code their stuff on your schedule.
          </p>

          {/* Kid rows */}
          <div className="flex flex-col w-full" style={{ gap: 14 }}>
            {kids.map((kid, idx) => {
              const showClear = kid.name.trim().length > 0 || kids.length > 1;
              const labelName = kid.name.trim() ? cleanName(kid.name) : null;
              return (
                <div
                  key={idx}
                  className="w-full"
                  style={{
                    backgroundColor: "#F8F8FF",
                    border: "1px solid #E0E0F8",
                    borderRadius: 12,
                    padding: "12px 14px",
                  }}
                >
                  {/* Name input */}
                  <div className="relative w-full">
                    <input
                      type="text"
                      value={kid.name}
                      placeholder="Kid's name"
                      onChange={(e) => updateName(idx, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (idx === kids.length - 1 && kid.name.trim() && kids.length < MAX_KIDS) {
                            addField();
                          } else {
                            handleNext();
                          }
                        }
                      }}
                      className="w-full bg-white"
                      style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 16,
                        color: "#1C1A3E",
                        border: "1px solid #C8C8F4",
                        borderRadius: 10,
                        padding: "11px 40px 11px 14px",
                        outline: "none",
                      }}
                      aria-label={`Kid name ${idx + 1}`}
                    />
                    {showClear && (
                      <button
                        type="button"
                        onClick={() => removeField(idx)}
                        aria-label="Remove this kid"
                        className="absolute top-1/2 -translate-y-1/2 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
                        style={{
                          right: 8,
                          width: 26,
                          height: 26,
                          color: "#54525C",
                        }}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>

                  {/* Birthday input */}
                  <div style={{ marginTop: 10 }}>
                    <label
                      style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 13,
                        color: "#54525C",
                        display: "block",
                        marginBottom: 2,
                      }}
                    >
                      {labelName ? `When's ${labelName}'s birthday?` : "When's their birthday?"}
                      <span
                        style={{
                          display: "block",
                          fontSize: 12,
                          color: "#8B8B9E",
                          fontWeight: 400,
                          marginTop: 1,
                        }}
                      >
                        (optional — helps Kin plan ahead 🎂)
                      </span>
                    </label>
                    <input
                      type="date"
                      value={kid.birthday}
                      onChange={(e) => updateBirthday(idx, e.target.value)}
                      className="w-full bg-white"
                      style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 15,
                        color: kid.birthday ? "#1C1A3E" : "#8B8B9E",
                        border: "1px solid #C8C8F4",
                        borderRadius: 10,
                        padding: "10px 14px",
                        outline: "none",
                      }}
                      aria-label={`Birthday for kid ${idx + 1}`}
                    />
                  </div>

                  {/* #1853 — boy/girl. Optional, and "neither" is a real
                      answer: Kin says "they" until she picks one. Tapping the
                      selected pill again clears it, so a mistap on the way
                      through onboarding is not permanent. */}
                  <div style={{ marginTop: 10 }}>
                    <label
                      style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 13,
                        color: "#54525C",
                        display: "block",
                        marginBottom: 6,
                      }}
                    >
                      {labelName ? `Is ${labelName} a boy or a girl?` : "Boy or girl?"}
                      <span
                        style={{
                          display: "block",
                          fontSize: 12,
                          color: "#8B8B9E",
                          fontWeight: 400,
                          marginTop: 1,
                        }}
                      >
                        (optional — so Kin gets it right 💜)
                      </span>
                    </label>
                    <div
                      className="flex items-center"
                      style={{ gap: 8 }}
                      role="group"
                      aria-label={`Boy or girl for kid ${idx + 1}`}
                    >
                      {KID_GENDER_VALUES.map((g) => {
                        const selected = kid.gender === g;
                        return (
                          <button
                            key={g}
                            type="button"
                            onClick={() => updateGender(idx, g)}
                            aria-pressed={selected}
                            aria-label={`${KID_GENDER_LABELS[g]} — kid ${idx + 1}`}
                            style={{
                              fontFamily: "'Plus Jakarta Sans', sans-serif",
                              fontSize: 15,
                              fontWeight: selected ? 600 : 400,
                              color: selected ? "#FFFFFF" : "#54525C",
                              backgroundColor: selected ? "#5050C8" : "#FFFFFF",
                              border: `1px solid ${selected ? "#5050C8" : "#C8C8F4"}`,
                              borderRadius: 999,
                              padding: "8px 20px",
                              outline: "none",
                            }}
                          >
                            {KID_GENDER_LABELS[g]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add another link */}
          {kids.length < MAX_KIDS && (
            <button
              type="button"
              onClick={addField}
              className="self-start mt-3 hover:underline transition-all"
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 14,
                fontWeight: 600,
                color: "#5050C8",
              }}
            >
              + Add another
            </button>
          )}

          {/* Next button */}
          <button
            type="button"
            onClick={handleNext}
            disabled={saving}
            className="w-full mt-6 transition-all hover:opacity-90 disabled:opacity-60"
            style={{
              backgroundColor: "#5050C8",
              color: "#FFFFFF",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 16,
              fontWeight: 600,
              padding: "14px 20px",
              borderRadius: 14,
              border: "none",
              cursor: saving ? "default" : "pointer",
            }}
          >
            {saving ? "Saving…" : "Next"}
          </button>

          {/* Skip link */}
          <button
            type="button"
            onClick={handleSkip}
            disabled={saving}
            className="mt-3 hover:underline transition-all"
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 14,
              color: "#54525C",
              background: "none",
              border: "none",
              cursor: saving ? "default" : "pointer",
            }}
          >
            Skip for now
          </button>

          {/* Reassurance */}
          <p
            className="text-center mt-4"
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 14,
              fontWeight: 500,
              color: "#5050C8",
              lineHeight: 1.4,
            }}
          >
            You can always add or change kids in Settings. 💜
          </p>
        </div>
      </div>
    </div>
  );
};

export default KidsCrewSetup;
